import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, MagnifyingGlass, UploadSimple, MapPin, Wrench, Storefront, Certificate, FilePdf, Buildings, Stack, SquaresFour } from "@phosphor-icons/react";
import { PhotoTile } from "@/components/PhotoTile";
import { ImportCsv } from "@/components/ImportCsv";
import { ComplianceStatusBadge, ComplianceModal } from "@/pages/Compliance";

const STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  under_repair: { label: "Under repair", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  retired: { label: "Retired", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.active;
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm ${m.cls}`} data-testid={`equipment-status-${status}`}>{m.label}</span>;
}

const blankEq = { store_id: "", name: "", category: "", model: "", serial_number: "", manufacturer: "", location: "", storage_location: "", purchase_date: "", warranty_expiry: "", status: "active", notes: "", photos: [], maintenance_company_id: "" };
const EQUIPMENT_INVALIDATE = ["equipment"];

export default function Equipment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const storeFilter = sp.get("store_id") || "";
  const [q, setQ] = useState("");

  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: equipment = [] } = useQuery({
    queryKey: ["equipment", storeFilter],
    queryFn: () => http.get("/equipment", { params: storeFilter ? { store_id: storeFilter } : {} }).then(r => r.data),
  });
  const { data: docTypes = {} } = useQuery({ queryKey: ["compliance-doc-types"], queryFn: () => http.get("/compliance/doc-types").then(r => r.data) });
  const { data: maintenanceCompanies = [] } = useQuery({ queryKey: ["maintenance-companies"], queryFn: () => http.get("/maintenance-companies").then(r => r.data) });
  const mcMap = Object.fromEntries(maintenanceCompanies.map(m => [m.id, m]));
  const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));

  const canCreate = ["admin", "general_manager", "maintenance_manager", "district_manager", "area_manager", "store_manager"].includes(user?.role);
  const canDelete = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);

  const filtered = useMemo(() => equipment.filter(e =>
    !q || e.name.toLowerCase().includes(q.toLowerCase()) || (e.category || "").toLowerCase().includes(q.toLowerCase()) || (e.model || "").toLowerCase().includes(q.toLowerCase()) || (e.serial_number || "").toLowerCase().includes(q.toLowerCase())
  ), [equipment, q]);

  const [groupBy, setGroupBy] = useState(false);
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const e of filtered) {
      const k = e.category || "Uncategorised";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupBy]);

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blankEq);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [complianceModal, setComplianceModal] = useState(null);

  const { data: eqDocs = [] } = useQuery({
    queryKey: ["compliance", "equipment", modal?.id],
    queryFn: () => http.get("/compliance", { params: { entity_type: "equipment", entity_id: modal.id } }).then(r => r.data),
    enabled: !!modal?.id,
  });

  const viewFile = async (fid) => {
    try {
      const r = await http.get(`/files/${fid}`, { responseType: "blob", validateStatus: () => true });
      if (r.status < 200 || r.status >= 300) { alert("File missing or expired."); return; }
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { console.error("[equipment] viewFile failed", e); }
  };
  const removeCompliance = async (docId) => {
    if (!window.confirm("Soft-delete this compliance document?")) return;
    try { await http.delete(`/compliance/${docId}`); qc.invalidateQueries({ queryKey: ["compliance", "equipment", modal.id] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const open = (mode, data = null) => {
    setForm(data ? { ...blankEq, ...data } : { ...blankEq, store_id: storeFilter || stores[0]?.id || "" });
    setModal({ mode, id: data?.id }); setErr("");
  };
  const close = () => setModal(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = { ...form };
      if (modal.mode === "create") await http.post("/equipment", body);
      else await http.patch(`/equipment/${modal.id}`, body);
      qc.invalidateQueries({ queryKey: ["equipment"] });
      close();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete equipment "${name}"?`)) return;
    try { await http.delete(`/equipment/${id}`); qc.invalidateQueries({ queryKey: ["equipment"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const onUploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !modal?.id) return;
    setUploadingPhotos(true); setErr("");
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      const r2 = await http.post(`/equipment/${modal.id}/photos`, { file_ids: ids });
      setForm({ ...form, photos: r2.data.photos });
      qc.invalidateQueries({ queryKey: ["equipment"] });
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setUploadingPhotos(false); e.target.value = ""; }
  };
  const removePhoto = async (fid) => {
    if (!modal?.id) return;
    try {
      const r = await http.delete(`/equipment/${modal.id}/photos/${fid}`);
      setForm({ ...form, photos: r.data.photos });
      qc.invalidateQueries({ queryKey: ["equipment"] });
    } catch (e) { console.error("[equipment] removePhoto failed", e); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Inventory</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Equipment</h1>
          <p className="text-sm text-slate-600 mt-1">{filtered.length} asset{filtered.length === 1 ? "" : "s"} {storeFilter && <>· filtered to <span className="font-medium">{storeMap[storeFilter]}</span></>}</p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <ImportCsv resource="equipment" invalidateKeys={EQUIPMENT_INVALIDATE} />
            {stores.length > 0 && (
              <button onClick={() => open("create")} data-testid="equipment-new-button"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
                <Plus size={14} weight="bold" /> Add equipment
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="equipment-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, model, serial…"
            className="h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md w-72 outline-none focus:border-[#002FA7]" />
        </div>
        <select value={storeFilter} onChange={e => setSp(e.target.value ? { store_id: e.target.value } : {})} data-testid="equipment-store-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7]">
          <option value="">All stores</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="inline-flex border border-slate-200 rounded-md overflow-hidden ml-auto">
          <button onClick={() => setGroupBy(false)} data-testid="equipment-view-flat"
            className={`px-3 h-9 text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1.5 ${!groupBy ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}>
            <SquaresFour size={12} /> Flat
          </button>
          <button onClick={() => setGroupBy(true)} data-testid="equipment-view-grouped"
            className={`px-3 h-9 text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1.5 ${groupBy ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}>
            <Stack size={12} /> By category
          </button>
        </div>
      </div>

      {!groupBy ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(e => (
            <EquipmentCard key={e.id} e={e} storeMap={storeMap} mcMap={mcMap} canCreate={canCreate} canDelete={canDelete} onEdit={() => open("edit", e)} onDelete={() => remove(e.id, e.name)} />
          ))}
          {filtered.length === 0 && <div className="col-span-full py-12 text-center text-sm text-slate-500" data-testid="equipment-empty">No equipment matches.</div>}
        </div>
      ) : (
        <div className="space-y-8" data-testid="equipment-grouped">
          {grouped?.map(([cat, items]) => (
            <section key={cat} data-testid={`equipment-group-${cat}`}>
              <div className="flex items-baseline gap-3 mb-3 pb-2 border-b border-slate-200">
                <h2 className="text-lg font-heading font-semibold tracking-tight">{cat}</h2>
                <span className="text-[11px] font-mono-data text-slate-500">{items.length} asset{items.length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(e => (
                  <EquipmentCard key={e.id} e={e} storeMap={storeMap} mcMap={mcMap} canCreate={canCreate} canDelete={canDelete} onEdit={() => open("edit", e)} onDelete={() => remove(e.id, e.name)} />
                ))}
              </div>
            </section>
          ))}
          {filtered.length === 0 && <div className="py-12 text-center text-sm text-slate-500" data-testid="equipment-empty">No equipment matches.</div>}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="equipment-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div><div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Equipment</h2></div>
              <button type="button" onClick={close} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Store *</label>
                <select required value={form.store_id} onChange={e => setForm({ ...form, store_id: e.target.value })} data-testid="equipment-store-select" className="rmms-input">
                  <option value="">Select…</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="equipment-name-input" className="rmms-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="overline mb-1.5 block">Category</label>
                <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="equipment-category-input" className="rmms-input" placeholder="Fryer / Cooler / POS…" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Manufacturer</label>
                <input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} data-testid="equipment-manufacturer-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Model</label>
                <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} data-testid="equipment-model-input" className="rmms-input font-mono-data" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Serial number</label>
                <input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} data-testid="equipment-serial-input" className="rmms-input font-mono-data" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="equipment-status-select" className="rmms-input">
                  <option value="active">Active</option>
                  <option value="under_repair">Under repair</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            </div>

            <div>
              <label className="overline mb-1.5 block">Installed location within store</label>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="equipment-location-input" className="rmms-input" placeholder="e.g. Kitchen back wall — bay 2" />
            </div>

            <div>
              <label className="overline mb-1.5 block">Storage location <span className="text-slate-400 normal-case tracking-normal">(if stored / spare)</span></label>
              <input value={form.storage_location || ""} onChange={e => setForm({ ...form, storage_location: e.target.value })} data-testid="equipment-storage-location-input" className="rmms-input" placeholder="e.g. Stockroom shelf B3" />
            </div>

            <div>
              <label className="overline mb-1.5 block">Maintenance company <span className="text-slate-400 normal-case tracking-normal">(optional)</span></label>
              <select value={form.maintenance_company_id || ""} onChange={e => setForm({ ...form, maintenance_company_id: e.target.value || null })} data-testid="equipment-mc-select" className="rmms-input">
                <option value="">(none — no contract)</option>
                {maintenanceCompanies.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Purchase date</label>
                <input type="date" value={form.purchase_date || ""} onChange={e => setForm({ ...form, purchase_date: e.target.value })} data-testid="equipment-purchase-input" className="rmms-input font-mono-data" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Warranty expiry</label>
                <input type="date" value={form.warranty_expiry || ""} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} data-testid="equipment-warranty-input" className="rmms-input font-mono-data" />
              </div>
            </div>

            <div>
              <label className="overline mb-1.5 block">Notes</label>
              <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="equipment-notes-input" className="w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#002FA7]" />
            </div>

            {modal.mode === "edit" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline">Photos</label>
                  <label data-testid="equipment-photo-upload-label" className="text-xs inline-flex items-center gap-1.5 cursor-pointer h-8 px-3 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors">
                    <UploadSimple size={12} weight="bold" />
                    {uploadingPhotos ? "Uploading…" : "Upload"}
                    <input data-testid="equipment-photo-upload-input" type="file" accept="image/*" multiple onChange={onUploadPhotos} className="hidden" />
                  </label>
                </div>
                {(form.photos && form.photos.length > 0) ? (
                  <div className="grid grid-cols-4 gap-2">
                    {form.photos.map(fid => <PhotoTile key={fid} fileId={fid} onRemove={removePhoto} canRemove />)}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">No photos yet.</div>
                )}
              </div>
            )}

            {modal.mode === "edit" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline flex items-center gap-1.5"><Certificate size={11} /> Compliance documents</label>
                  <button type="button" onClick={() => setComplianceModal({ entity_type: "equipment", entity_id: modal.id, doc_type: "pat_test" })} data-testid="equipment-compliance-add"
                    className="text-xs inline-flex items-center gap-1.5 h-8 px-3 border border-slate-200 rounded-sm hover:bg-slate-50">
                    <Plus size={11} weight="bold" /> Upload cert
                  </button>
                </div>
                {eqDocs.length === 0 ? (
                  <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-sm">No PAT / calibration certificates yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {eqDocs.map(d => (
                      <div key={d.id} data-testid={`equipment-compliance-row-${d.id}`} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-3 py-2 border border-slate-200 rounded-sm">
                        <div className="min-w-0">
                          <div className="text-xs font-medium">{docTypes[d.doc_type] || d.doc_type}</div>
                          <div className="text-[10px] text-slate-500 truncate">{d.expiry_date ? `Expires ${d.expiry_date}` : "No expiry"}{d.issued_by && <> · {d.issued_by}</>}</div>
                        </div>
                        <ComplianceStatusBadge status={d.status} />
                        <div className="flex gap-1">
                          {d.file_id && <button type="button" onClick={() => viewFile(d.file_id)} data-testid={`equipment-compliance-view-${d.id}`} className="h-6 px-2 text-[10px] inline-flex items-center gap-1 border border-slate-200 rounded-sm hover:bg-slate-50"><FilePdf size={10} weight="bold" /></button>}
                          <button type="button" onClick={() => setComplianceModal({ ...d })} data-testid={`equipment-compliance-edit-${d.id}`} className="h-6 w-6 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={10} /></button>
                          {canDelete && <button type="button" onClick={() => removeCompliance(d.id)} data-testid={`equipment-compliance-delete-${d.id}`} className="h-6 w-6 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={10} /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={close} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="equipment-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ComplianceModal
        open={!!complianceModal}
        onClose={() => setComplianceModal(null)}
        presets={complianceModal || {}}
        onSaved={() => qc.invalidateQueries({ queryKey: ["compliance", "equipment", modal?.id] })}
      />
    </div>
  );
}

function EquipmentCard({ e, storeMap, mcMap, canCreate, canDelete, onEdit, onDelete }) {
  return (
    <div data-testid={`equipment-card-${e.id}`} className="rmms-card rmms-card-hover group relative">
      <Link to={`/equipment/${e.id}`} data-testid={`equipment-link-${e.id}`} className="block">
        <div className="aspect-video bg-slate-100 border-b border-slate-200 relative overflow-hidden">
          {e.photos && e.photos.length > 0 ? (
            <PhotoTile fileId={e.photos[0]} canRemove={false} size="lg" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-300"><Wrench size={48} weight="thin" /></div>
          )}
          <div className="absolute top-2 left-2">
            <StatusBadge status={e.status} />
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-heading uppercase tracking-wider text-slate-500">{e.category || "Uncategorised"}</span>
          </div>
          <div className="font-medium leading-tight">{e.name}</div>
          {e.model && <div className="text-xs text-slate-500 font-mono-data mt-0.5">{e.manufacturer} · {e.model}</div>}
          {e.serial_number && <div className="text-[11px] text-slate-400 font-mono-data mt-0.5">SN: {e.serial_number}</div>}
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 gap-1.5 text-[12px]">
            <div className="flex items-center gap-1.5 text-slate-600"><Storefront size={11} />{storeMap[e.store_id]}</div>
            {e.location && <div className="flex items-center gap-1.5 text-slate-600"><MapPin size={11} />{e.location}</div>}
            {e.maintenance_company_id && mcMap[e.maintenance_company_id] && (
              <div className="flex items-center gap-1.5 text-slate-600"><Buildings size={11} />{mcMap[e.maintenance_company_id].name}</div>
            )}
          </div>
        </div>
      </Link>
      {(canCreate || canDelete) && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canCreate && <button onClick={(ev) => { ev.preventDefault(); onEdit(); }} data-testid={`equipment-edit-${e.id}`} className="h-7 w-7 bg-white/95 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
          {canDelete && <button onClick={(ev) => { ev.preventDefault(); onDelete(); }} data-testid={`equipment-delete-${e.id}`} className="h-7 w-7 bg-white/95 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
        </div>
      )}
    </div>
  );
}
